import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { FiFile, FiFilm, FiTrash2, FiEye } from 'react-icons/fi';
import scansImage from '../../assets/scans_00000.png'; 

export const SweepAnimation = ({ animationClass }: { animationClass: string }) => {
    const pathD = { 
        'ultrasound-sweep-1': 'M 50 90 V 65 M 85 65 H 15', 
        'ultrasound-sweep-2': 'M 25 35 V 85',
        'ultrasound-sweep-3': 'M 50 35 V 85',
        'ultrasound-sweep-4': 'M 75 35 V 85',
        'ultrasound-sweep-5': 'M 15 50 H 85',
        'ultrasound-sweep-6': 'M 15 75 H 85',
        'ultrasound-sweep-7': 'M 20 80 Q 50 100 80 80' 
    }[animationClass] || '';

    return ( 
        <div className="w-full h-full rounded-md overflow-hidden relative group pointer-events-none bg-slate-200 dark:bg-gray-700">
            <div className="absolute inset-0 w-full h-full opacity-50" style={{ backgroundImage: `url(${scansImage})`, backgroundSize: "cover", backgroundPosition: "center" }}></div>
            <svg className="absolute inset-0 w-full h-full z-10" viewBox="0 0 100 100" preserveAspectRatio="none"> 
                <path d={pathD} fill="none" stroke="rgba(59, 130, 246, 0.6)" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 4" className="path-anim" /> 
            </svg> 
            <div className={`absolute w-3 h-5 bg-blue-600 rounded-sm shadow-lg border border-white dark:border-gray-800 z-20 ${animationClass}`} /> 
        </div> 
    );
};

export const SweepSlot = ({ sweep, fileItem, targetPatientId, onDrop, onRemove, onPreview, onClickSlot }: any) => {
    const ref = useRef<HTMLDivElement>(null);
    
    const [{ isOver, canDrop }, drop] = useDrop({ 
        accept: 'SWEEP_FILE', 
        drop: (item: any) => onDrop(item), 
        collect: (m) => ({ isOver: !!m.isOver(), canDrop: !!m.canDrop() }) 
    });
    
    const [{ isDragging }, drag] = useDrag({ 
        type: 'SWEEP_FILE', 
        item: { sweepId: sweep.id }, 
        canDrag: !!fileItem, 
        collect: (m) => ({ isDragging: !!m.isDragging() }) 
    });
    
    drag(drop(ref));

    let borderClass = 'border-dashed border-slate-300 bg-white dark:bg-gray-800 dark:border-gray-600';
    if (fileItem) {
        if (fileItem.dicomData && targetPatientId && fileItem.dicomData.patientId === targetPatientId) 
            borderClass = 'border-green-400 bg-green-50 dark:bg-green-900/20 dark:border-green-500/50 border-2';
        else if (fileItem.dicomData && targetPatientId && fileItem.dicomData.patientId !== targetPatientId) 
            borderClass = 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-500/50 border-2';
        else 
            borderClass = 'border-green-500 bg-white dark:bg-gray-800 dark:border-green-600 border-2';
    } else if (isOver && canDrop) {
        borderClass = 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 border-dashed';
    }

    return (
        <div ref={ref} 
        onClick={() => !fileItem && onClickSlot()} 
        className={`cursor-pointer relative aspect-[3/4] rounded-xl transition-all overflow-hidden shadow-sm ${borderClass}`}>
            <div className="absolute top-0 left-0 bg-slate-100/90 dark:bg-gray-700/90 dark:text-gray-200 px-2 py-0.5 text-[10px] font-bold z-10 border-br rounded-br shadow-sm">
                {sweep.name}
            </div>
            
            {fileItem ? (
                <div className={`w-full h-full flex flex-col items-center justify-center p-2 group cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-50' : ''}`}>
                    <div className={`text-3xl mb-2 ${fileItem.type === 'dicom' ? 'text-blue-500 dark:text-blue-400' : 'text-purple-500 dark:text-purple-400'}`}>
                        {fileItem.type === 'dicom' ? <FiFile/> : <FiFilm/>}
                    </div>
                    <span className="text-[10px] truncate w-full text-center font-medium px-1 dark:text-gray-300">
                        {fileItem.file.name}
                    </span>
                    {fileItem.dicomData && (
                        <span className="text-[9px] bg-white/50 dark:bg-black/30 px-1 rounded text-slate-500 dark:text-gray-400 mt-1 max-w-full truncate">
                            {fileItem.dicomData.patientId}
                        </span>
                    )}
                    
                    <div className="absolute inset-0 bg-black/60 hidden group-hover:flex items-center justify-center gap-2 backdrop-blur-[1px]">
                        <button onClick={(e) => { e.stopPropagation(); onPreview(); }} className="text-white hover:text-blue-300 p-1.5 rounded-full hover:bg-white/10 transition-colors">
                            <FiEye size={18}/>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="text-white hover:text-red-300 p-1.5 rounded-full hover:bg-white/10 transition-colors">
                            <FiTrash2 size={18}/>
                        </button>
                    </div>
                </div>
            ) : (
                <SweepAnimation animationClass={sweep.animationClass} />
            )}
        </div>
    );
};